// src/web/routes/_authenticated/browse/index.tsx

import { AdjustmentsHorizontalIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { AssetGrid, useAssetSelection } from '@/components/features/AssetGrid'
import { DownloadModal } from '@/components/features/DownloadModal'
import { ActiveFilters, FilterPanel } from '@/components/features/FilterPanel'
import { GridToolbar, SelectionToolbar } from '@/components/features/GridToolbar'
import { Pagination } from '@/components/features/Pagination'

import { type Asset, useAssets } from '@/lib/hooks/useAssets'
import { useIsMobile } from '@/lib/hooks/useMediaQuery'
import { useDamPermissions } from '@/lib/hooks/usePermissions'
import { cn } from '@/lib/utils'
import { useFilterStore } from '@/stores/filters'

export const Route = createFileRoute('/_authenticated/browse/')({
	component: AssetsPage,
	// ?folder=<id> deep link (activity feed): opens browse scoped to that folder
	validateSearch: (search: Record<string, unknown>): { folder?: string } =>
		typeof search.folder === 'string' && search.folder ? { folder: search.folder } : {},
})

function AssetsPage() {
	const isMobile = useIsMobile()
	const { canWrite } = useDamPermissions()
	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false) // Default closed
	const { data: assetsData } = useAssets()
	const { selectedAssetIds, toggleSelection, selectAll, clearSelection, selectedCount } =
		useAssetSelection()
	const { setSelectedFolder } = useFilterStore()

	// Download modal state
	const [downloadModalOpen, setDownloadModalOpen] = useState(false)
	const [downloadFile, setDownloadFile] = useState<{
		id: string
		filename: string
		size: number
		fileType: string | null
		width?: number | null
		height?: number | null
		mediaTypeType?: string | null
		duration?: number | null
		thumbnailUrl?: string | null
	} | null>(null)

	const { folder: folderParam } = Route.useSearch()

	// ?folder deep link wins; otherwise clear any folder carried over from home
	useEffect(() => {
		if (folderParam) {
			setSelectedFolder(folderParam)
			return
		}
		const folder = useFilterStore.getState().selectedFolder
		if (folder) {
			setSelectedFolder(null)
		}
	}, [setSelectedFolder, folderParam])

	// Auto-open filter panel on desktop, auto-close on mobile
	useEffect(() => {
		setIsFilterPanelOpen(!isMobile)
	}, [isMobile])

	const handleDownload = useCallback((asset: Asset) => {
		if (asset.file) {
			setDownloadFile({
				id: asset.file.id,
				filename: asset.file.filename,
				size: asset.file.size,
				fileType: asset.file.fileType,
				width: asset.file.width,
				height: asset.file.height,
				mediaTypeType: asset.file.mediaTypeType,
				duration: asset.file.duration,
				thumbnailUrl: asset.thumbnailUrl,
			})
			setDownloadModalOpen(true)
		}
	}, [])

	const handleSelectAll = () => {
		if (assetsData?.assets) {
			selectAll(assetsData.assets.map((a) => a.id))
		}
	}

	return (
		<div className="flex gap-3 h-full overflow-hidden px-8 sm:px-14 py-3 pt-20">
			{/* Filter Panel - Desktop: sidebar, Mobile: fullscreen overlay */}
			{isMobile ? (
				// Mobile: Full-screen overlay
				<>
					{isFilterPanelOpen && (
						<button
							type="button"
							className="fixed inset-0 bg-black/20 z-40 cursor-default"
							onClick={() => setIsFilterPanelOpen(false)}
							aria-label="Close filters"
						/>
					)}
					<aside
						className={cn(
							'fixed inset-y-0 left-0 z-50 w-full max-w-[320px] bg-background',
							'transform transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
							isFilterPanelOpen ? 'translate-x-0' : '-translate-x-full'
						)}
					>
						<div className="flex items-center justify-between px-4 py-3 border-b border-border">
							<h2 className="font-semibold text-lg">Filters</h2>
							<button
								type="button"
								onClick={() => setIsFilterPanelOpen(false)}
								className="p-2 -mr-2 rounded-lg hover:bg-muted transition-colors"
								aria-label="Close filters"
							>
								<XMarkIcon className="w-5 h-5" />
							</button>
						</div>
						<FilterPanel className="h-[calc(100%-57px)]" />
					</aside>
				</>
			) : (
				// Desktop: Sidebar — rounded card, detached from edge
				<aside
					className={cn(
						'flex-shrink-0 h-full overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
						isFilterPanelOpen ? 'w-[380px] py-3 pr-0' : 'w-0'
					)}
				>
					<FilterPanel className="h-full rounded-xl border border-border/50 bg-background overflow-hidden" />
				</aside>
			)}

			{/* Main Content */}
			<main className="flex-1 flex flex-col min-w-0 min-h-0 h-full overflow-hidden">
				{/* Toolbar */}
				<div className="py-3 sm:py-4">
					<div className="flex items-center gap-3">
						{/* Filter panel toggle */}
						<button
							type="button"
							onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
							className={cn(
								'flex items-center justify-center w-9 h-9 rounded-lg transition-all flex-shrink-0',
								isFilterPanelOpen && !isMobile
									? 'bg-primary/10 text-primary'
									: 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
							)}
							aria-label={isFilterPanelOpen ? 'Close filters' : 'Open filters'}
						>
							{isFilterPanelOpen && !isMobile ? (
								<XMarkIcon className="w-5 h-5" />
							) : (
								<AdjustmentsHorizontalIcon className="w-5 h-5" />
							)}
						</button>

						{selectedCount > 0 ? (
							<SelectionToolbar
								selectedCount={selectedCount}
								totalCount={assetsData?.total ?? 0}
								onSelectAll={handleSelectAll}
								onClearSelection={clearSelection}
								className="flex-1"
							/>
						) : (
							<GridToolbar
								totalCount={assetsData?.total ?? 0}
								currentPage={assetsData?.page ?? 1}
								pageSize={assetsData?.pageSize ?? 50}
								className="flex-1"
							/>
						)}
					</div>

					{/* Active filters */}
					<ActiveFilters className="mt-3" />
				</div>

				{/* Asset Grid */}
				<div className="flex-1 overflow-auto py-4 sm:py-6">
					<AssetGrid
						selectedAssetIds={selectedAssetIds}
						onAssetSelect={toggleSelection}
						onDownload={canWrite ? handleDownload : undefined}
						showAddToCollection={canWrite}
					/>
				</div>

				{/* Pagination */}
				{assetsData && assetsData.totalPages > 0 && (
					<footer className="py-3 sm:py-4">
						<Pagination totalItems={assetsData.total} totalPages={assetsData.totalPages} />
					</footer>
				)}
			</main>

			{/* Download Modal */}
			<DownloadModal
				open={downloadModalOpen}
				onOpenChange={setDownloadModalOpen}
				file={downloadFile}
			/>
		</div>
	)
}
